<template>
  <div class="row product-documentation-page">
    <div class="col-xl-12 order-xl-1">
      <header class="halloween-record-heading">
        <h1>Documentations produits</h1>
        <p>La bibliothèque des produits</p>
      </header>
      <spinner v-show="!isLoaded"></spinner>

      <card v-show="isLoaded" no-footer-line>
        <div slot="header" class="documentation-list-toolbar">
          <div class="documentation-toolbar-main">
            <div class="documentation-toolbar-title">
              <h4 class="card-title">Documentations produits</h4>
              <span class="documentation-toolbar-count">
                <strong>{{ filteredDocumentations.length }}</strong>
                <span>{{ filteredDocumentations.length > 1 ? "docs" : "doc" }}</span>
                <template v-if="filteredDocumentations.length !== documentations.length">
                  <span>sur {{ documentations.length }}</span>
                </template>
              </span>
            </div>
            <n-button
              type="primary"
              class="btn btn-sm btn-primary documentation-add-button"
              title="Ajouter"
              @click.native="openCreateModal"
            >
              <span><i class="fas fa-plus mr-2"></i>Ajouter</span>
            </n-button>
          </div>

          <div class="documentation-toolbar-filters">
            <div class="documentation-search-wrap">
              <i class="fas fa-search" aria-hidden="true"></i>
              <input
                v-model="searchText"
                type="text"
                placeholder="Recherche"
                class="documentation-search form-control"
              >
            </div>
            <el-select
              v-model="selectedProductFilter"
              class="documentation-product-filter"
              :disabled="productFilterOptions.length === 0"
              clearable
              filterable
              placeholder="Tous les produits"
              popper-class="documentation-product-select-popper"
              title="Filtrer par produit"
            >
              <el-option
                v-for="product in productFilterOptions"
                :key="product.value"
                :label="product.label"
                :value="product.value"
              >
                <div class="product-filter-option">
                  <div class="product-filter-option-copy">
                    <strong>{{ product.name }}</strong>
                    <span>{{ product.reference }}</span>
                  </div>
                  <span class="product-filter-option-count">
                    {{ product.count }} doc{{ product.count > 1 ? "s" : "" }}
                  </span>
                </div>
              </el-option>
            </el-select>
            <div class="documentation-language-filter" role="radiogroup" aria-label="Filtrer par langue">
              <button
                v-for="language in languageFilterOptions"
                :key="language.value || 'all'"
                type="button"
                class="language-filter-choice"
                :class="{ active: selectedLanguageFilter === language.value }"
                :aria-pressed="selectedLanguageFilter === language.value"
                :title="language.value ? language.label : 'Toutes les langues'"
                @click="selectedLanguageFilter = language.value"
              >
                <span
                  v-if="language.value"
                  class="language-flag"
                  :class="languageFlagClass(language.value)"
                  aria-hidden="true"
                ></span>
                <i v-else class="fas fa-globe-europe" aria-hidden="true"></i>
                <span>{{ language.shortLabel }}</span>
              </button>
            </div>
          </div>
        </div>

        <div v-if="filteredDocumentations.length > 0" class="documentation-grid">
          <div
            v-for="documentation in filteredDocumentations"
            :key="documentation.id"
            class="documentation-card"
          >
            <div class="documentation-card-top">
              <div class="documentation-document-icon">
                <i :class="fileIcon(documentation)"></i>
              </div>
              <div class="documentation-card-heading">
                <h5>{{ documentation.title }}</h5>
                <div class="documentation-product-line">
                  <strong>{{ documentation.product_name }}</strong>
                  <span class="documentation-card-reference">
                    {{ documentation.product_reference }}
                  </span>
                </div>
              </div>
              <span
                class="documentation-language-badge"
                :title="languageLabel(documentation.language)"
              >
                <span
                  class="language-flag"
                  :class="languageFlagClass(documentation.language)"
                  aria-hidden="true"
                ></span>
                <span>{{ languageShortLabel(documentation.language) }}</span>
              </span>
            </div>

            <div class="documentation-visual">
              <img
                v-if="documentation.has_illustration"
                :src="illustrationUrl(documentation)"
                :alt="documentation.product_name"
              >
              <div
                v-else-if="documentationQrPreviews[documentation.id]"
                class="documentation-visual-qr"
                v-html="documentationQrPreviews[documentation.id]"
              ></div>
              <div v-else class="documentation-visual-placeholder">
                <i class="fas fa-image"></i>
                <span>Aucune illustration</span>
              </div>
            </div>

            <p class="documentation-card-description">
              {{ documentation.description || "Aucune description" }}
            </p>

            <div v-if="documentation.current_version" class="documentation-version-card">
              <div class="version-card-header">
                <span>Dernière version</span>
                <strong>v{{ documentation.current_version.version }}</strong>
              </div>
              <div class="version-card-file">
                <i :class="fileIcon(documentation)"></i>
                <span>{{ documentation.current_version.original_name }}</span>
              </div>
              <div class="version-card-details">
                <span>{{ formatSize(documentation.current_version.size) }}</span>
                <span>{{ formatDate(documentation.updated_at) }}</span>
              </div>
            </div>

            <div class="documentation-card-actions">
              <n-button
                type="primary"
                class="btn btn-sm btn-primary"
                title="QR code"
                :disabled="!documentation.current_version"
                @click.native="openQr(documentation)"
              >
                <span><i class="fas fa-qrcode"></i></span>
              </n-button>
              <n-button
                type="primary"
                class="btn btn-sm btn-primary"
                title="Document d'accompagnement"
                :disabled="!documentation.has_qr_companion_pdf"
                @click.native="downloadQrCompanionPdf(documentation)"
              >
                <span><i class="fas fa-file-pdf"></i></span>
              </n-button>
              <n-button
                type="primary"
                class="btn btn-sm btn-primary"
                title="Télécharger"
                :disabled="!documentation.current_version"
                @click.native="openLatestDocumentation(documentation)"
              >
                <span><i class="fas fa-download"></i></span>
              </n-button>
              <n-button
                type="primary"
                class="btn btn-sm btn-primary"
                title="Nouvelle version"
                @click.native="openVersionModal(documentation)"
              >
                <span><i class="fas fa-upload"></i></span>
              </n-button>
              <n-button
                type="default"
                class="btn btn-sm btn-default"
                title="Modifier"
                @click.native="openEditDocumentationModal(documentation)"
              >
                <span><i class="fas fa-pen"></i></span>
              </n-button>
              <n-button
                type="default"
                class="btn btn-sm btn-default"
                title="Historique"
                @click.native="toggleHistory(documentation)"
              >
                <span><i class="fas fa-history"></i></span>
              </n-button>
            </div>

            <div v-if="expandedDocumentationId === documentation.id" class="documentation-history">
              <div
                v-for="version in documentation.versions"
                :key="version.id"
                class="history-row"
              >
                <div>
                  <strong>{{ version.version }}</strong>
                  <span>{{ version.original_name }}</span>
                  <small v-if="version.notes">{{ version.notes }}</small>
                </div>
                <div class="history-actions">
                  <span>{{ formatDate(version.created_at) }}</span>
                  <button
                    type="button"
                    class="history-icon-button history-download"
                    title="Télécharger"
                    @click="downloadVersion(documentation, version)"
                  >
                    <i class="fas fa-download"></i>
                  </button>
                  <button
                    type="button"
                    class="history-icon-button history-edit"
                    title="Modifier"
                    @click="openEditVersionModal(documentation, version)"
                  >
                    <i class="fas fa-pen"></i>
                  </button>
                  <button
                    type="button"
                    class="history-icon-button history-delete"
                    title="Supprimer"
                    @click="confirmDeleteVersion(documentation, version)"
                  >
                    <i class="fas fa-trash-alt"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="documentation-empty">
          Aucune documentation
        </div>
      </card>

      <modal
        :show.sync="showCreateModal"
        modal-classes="modal-dialog-centered documentation-modal-dialog"
        body-classes="documentation-modal-body"
        @close="closeCreateModal"
      >
        <h5 slot="header" class="modal-title">
          {{ isEditingDocumentation ? "Modifier la documentation" : "Ajouter une documentation" }}
        </h5>
        <form class="documentation-form" @submit.prevent="saveDocumentation">
          <div class="form-section-header">
            <span class="form-section-icon"><i class="fas fa-box-open"></i></span>
            <div>
              <strong>Produit</strong>
              <small>Identification visible dans la liste</small>
            </div>
          </div>
          <div class="modern-form-grid">
            <div class="modern-field">
              <label for="documentation-product-reference">Référence produit</label>
              <div class="modern-input-wrap">
                <i class="now-ui-icons shopping_tag-content"></i>
                <input
                  id="documentation-product-reference"
                  v-model="form.product_reference"
                  type="text"
                  placeholder="REF-001"
                  autocomplete="off"
                >
              </div>
            </div>
            <div class="modern-field">
              <label for="documentation-product-name">Nom produit</label>
              <div class="modern-input-wrap">
                <i class="now-ui-icons shopping_box"></i>
                <input
                  id="documentation-product-name"
                  v-model="form.product_name"
                  type="text"
                  placeholder="Nom du produit"
                  autocomplete="off"
                >
              </div>
            </div>
            <div class="modern-field modern-field-wide">
              <label for="documentation-title">Titre documentation</label>
              <div class="modern-input-wrap">
                <i class="now-ui-icons files_single-copy-04"></i>
                <input
                  id="documentation-title"
                  v-model="form.title"
                  type="text"
                  placeholder="Notice utilisateur"
                  autocomplete="off"
                >
              </div>
            </div>
            <div class="modern-field">
              <label>Langue</label>
              <div class="language-selector" role="radiogroup" aria-label="Langue de la documentation">
                <button
                  v-for="language in languageOptions"
                  :key="language.value"
                  type="button"
                  class="language-choice"
                  :class="{ active: form.language === language.value }"
                  :aria-pressed="form.language === language.value"
                  @click="form.language = language.value"
                >
                  <span
                    class="language-flag"
                    :class="languageFlagClass(language.value)"
                    aria-hidden="true"
                  ></span>
                  <span>{{ language.label }}</span>
                </button>
              </div>
            </div>
            <div v-if="!isEditingDocumentation" class="modern-field">
              <label for="documentation-version">Version</label>
              <div class="modern-input-wrap">
                <i class="now-ui-icons design_bullet-list-67"></i>
                <input
                  id="documentation-version"
                  v-model="form.version"
                  type="text"
                  placeholder="1.0"
                  autocomplete="off"
                >
              </div>
            </div>
          </div>

          <div class="form-section-header">
            <span class="form-section-icon"><i class="fas fa-qrcode"></i></span>
            <div>
              <strong>QR code</strong>
              <small>Adresse courte imprimee sur le support</small>
            </div>
          </div>
          <div class="modern-form-grid compact-section-grid">
            <div class="modern-field modern-field-full">
              <label for="documentation-accessibility-url">URL affichee dans le QR code</label>
              <div class="modern-input-wrap qr-input-wrap">
                <i class="now-ui-icons business_globe"></i>
                <input
                  id="documentation-accessibility-url"
                  v-model="form.accessibility_url"
                  type="url"
                  placeholder="https://ec2e.link/ref-produit"
                  autocomplete="off"
                >
              </div>
              <small class="field-helper">Un chemin comme /ESK/BookUtilisateur sera publie en ESK-BookUtilisateur.</small>
            </div>
          </div>

          <div class="form-section-header">
            <span class="form-section-icon"><i class="fas fa-cloud-upload-alt"></i></span>
            <div>
              <strong>Fichiers</strong>
              <small>
                PDF technique, illustration produit et document associe au QR code
              </small>
            </div>
          </div>
          <div class="file-upload-grid">
            <div class="modern-field">
              <label>Illustration produit</label>
              <div
                v-if="isEditingDocumentation && selectedDocumentation && selectedDocumentation.has_illustration"
                class="current-illustration-preview"
              >
                <img
                  :src="illustrationUrl(selectedDocumentation)"
                  :alt="selectedDocumentation.product_name"
                >
                <span>Illustration actuelle</span>
                <button
                  type="button"
                  class="current-file-delete-button"
                  title="Supprimer l'illustration"
                  :disabled="isSaving"
                  @click="confirmDeleteDocumentationIllustration"
                >
                  <i class="fas fa-trash-alt"></i>
                </button>
              </div>
              <label for="documentation-illustration-input" class="upload-dropzone image-upload-dropzone">
                <span class="upload-icon"><i class="fas fa-image"></i></span>
                <span class="upload-content">
                  <strong>{{ form.illustration ? form.illustration.name : illustrationUploadLabel }}</strong>
                  <small>{{ form.illustration ? formatSize(form.illustration.size) : "JPEG, PNG ou WebP, optionnel" }}</small>
                </span>
                <span class="upload-action">Parcourir</span>
              </label>
              <input
                id="documentation-illustration-input"
                ref="documentationIllustration"
                type="file"
                class="documentation-file-input"
                accept=".jpg,.jpeg,.png,.webp"
                @change="setDocumentationIllustration"
              >
            </div>
            <div v-if="!isEditingDocumentation" class="modern-field">
              <label>Document technique</label>
              <label for="documentation-file-input" class="upload-dropzone document-upload-dropzone">
                <span class="upload-icon"><i class="fas fa-file-upload"></i></span>
                <span class="upload-content">
                  <strong>{{ form.file ? form.file.name : "Choisir un fichier" }}</strong>
                  <small>{{ form.file ? formatSize(form.file.size) : "PDF principal de la documentation" }}</small>
                </span>
                <span class="upload-action">Parcourir</span>
              </label>
              <input
                id="documentation-file-input"
                ref="documentationFile"
                type="file"
                class="documentation-file-input"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx"
                @change="setDocumentationFile"
              >
            </div>
            <div class="modern-field">
              <label>Document d'accompagnement</label>
              <div
                v-if="isEditingDocumentation && selectedDocumentation && selectedDocumentation.has_qr_companion_pdf"
                class="current-companion-preview"
              >
                <i class="fas fa-file-pdf"></i>
                <span>
                  {{
                    selectedDocumentation.qr_companion_pdf && selectedDocumentation.qr_companion_pdf.original_name
                      ? selectedDocumentation.qr_companion_pdf.original_name
                      : "Document PDF actuel"
                  }}
                </span>
                <button
                  type="button"
                  class="current-file-delete-button"
                  title="Supprimer le document"
                  :disabled="isSaving"
                  @click="confirmDeleteDocumentationCompanionPdf"
                >
                  <i class="fas fa-trash-alt"></i>
                </button>
              </div>
              <label for="documentation-companion-pdf-input" class="upload-dropzone companion-upload-dropzone">
                <span class="upload-icon"><i class="fas fa-project-diagram"></i></span>
                <span class="upload-content">
                  <strong>{{ form.qr_companion_pdf ? form.qr_companion_pdf.name : companionPdfUploadLabel }}</strong>
                  <small>{{ form.qr_companion_pdf ? formatSize(form.qr_companion_pdf.size) : "PDF optionnel, schema de cablage par exemple" }}</small>
                </span>
                <span class="upload-action">Parcourir</span>
              </label>
              <input
                id="documentation-companion-pdf-input"
                ref="documentationCompanionPdf"
                type="file"
                class="documentation-file-input"
                accept=".pdf"
                @change="setDocumentationCompanionPdf"
              >
            </div>
          </div>

          <div class="form-section-header">
            <span class="form-section-icon"><i class="fas fa-align-left"></i></span>
            <div>
              <strong>Details</strong>
              <small>Contexte et suivi de version</small>
            </div>
          </div>
          <div class="modern-form-grid">
            <div class="modern-field modern-field-textarea">
              <label for="documentation-description">Description</label>
              <textarea
                id="documentation-description"
                v-model="form.description"
                class="modern-textarea"
                rows="3"
                placeholder="Usage, cible ou contenu principal"
              ></textarea>
            </div>
            <div v-if="!isEditingDocumentation" class="modern-field modern-field-textarea">
              <label for="documentation-version-notes">Notes version</label>
              <textarea
                id="documentation-version-notes"
                v-model="form.notes"
                class="modern-textarea"
                rows="3"
                placeholder="Changements de cette version"
              ></textarea>
            </div>
          </div>
        </form>
        <template slot="footer">
          <n-button
            type="primary"
            class="btn btn-sm btn-primary"
            :disabled="isSaving"
            @click.native="saveDocumentation"
          >
            <span><i class="fas fa-save mr-2"></i>{{ isEditingDocumentation ? "Mettre a jour" : "Enregistrer" }}</span>
          </n-button>
          <n-button type="default" class="btn btn-sm btn-default" @click.native="closeCreateModal">
            <span>Annuler</span>
          </n-button>
        </template>
      </modal>

      <modal
        :show.sync="showVersionModal"
        modal-classes="modal-dialog-centered documentation-modal-dialog"
        body-classes="documentation-modal-body"
        @close="closeVersionModal"
      >
        <h5 slot="header" class="modal-title">
          Nouvelle version
        </h5>
        <form class="documentation-form" @submit.prevent="saveVersion">
          <div v-if="selectedVersionDocumentation" class="selected-documentation">
            <span>{{ selectedVersionDocumentation.product_reference }}</span>
            <strong>{{ selectedVersionDocumentation.title }}</strong>
          </div>
          <div class="modern-form-grid version-form-grid">
            <div class="modern-field">
              <label for="new-documentation-version">Version</label>
              <div class="modern-input-wrap">
                <i class="now-ui-icons design_bullet-list-67"></i>
                <input
                  id="new-documentation-version"
                  v-model="versionForm.version"
                  type="text"
                  placeholder="1.1"
                  autocomplete="off"
                >
              </div>
            </div>
            <div class="modern-field modern-field-wide">
              <label>Nouveau fichier</label>
              <label for="version-file-input" class="upload-dropzone compact-upload">
                <span class="upload-icon"><i class="fas fa-cloud-upload-alt"></i></span>
                <span class="upload-content">
                  <strong>{{ versionForm.file ? versionForm.file.name : "Choisir un fichier" }}</strong>
                  <small>{{ versionForm.file ? formatSize(versionForm.file.size) : "PDF, image, Word ou Excel" }}</small>
                </span>
                <span class="upload-action">Parcourir</span>
              </label>
              <input
                id="version-file-input"
                ref="versionFile"
                type="file"
                class="documentation-file-input"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx"
                @change="setVersionFile"
              >
            </div>
            <div class="modern-field modern-field-full">
              <label for="new-documentation-version-notes">Notes version</label>
              <textarea
                id="new-documentation-version-notes"
                v-model="versionForm.notes"
                class="modern-textarea"
                rows="3"
              ></textarea>
            </div>
          </div>
        </form>
        <template slot="footer">
          <n-button
            type="primary"
            class="btn btn-sm btn-primary"
            :disabled="isSaving"
            @click.native="saveVersion"
          >
            <span><i class="fas fa-save mr-2"></i>Enregistrer</span>
          </n-button>
          <n-button type="default" class="btn btn-sm btn-default" @click.native="closeVersionModal">
            <span>Annuler</span>
          </n-button>
        </template>
      </modal>

      <modal
        :show.sync="showEditVersionModal"
        modal-classes="modal-dialog-centered documentation-modal-dialog"
        body-classes="documentation-modal-body"
        @close="closeEditVersionModal"
      >
        <h5 slot="header" class="modal-title">
          Modifier la version
        </h5>
        <form class="documentation-form" @submit.prevent="saveEditedVersion">
          <div v-if="selectedEditVersionDocumentation" class="selected-documentation">
            <span>{{ selectedEditVersionDocumentation.product_reference }}</span>
            <strong>{{ selectedEditVersionDocumentation.title }}</strong>
          </div>
          <div class="modern-form-grid version-form-grid">
            <div class="modern-field">
              <label for="edit-documentation-version">Version</label>
              <div class="modern-input-wrap">
                <i class="now-ui-icons design_bullet-list-67"></i>
                <input
                  id="edit-documentation-version"
                  v-model="editVersionForm.version"
                  type="text"
                  placeholder="1.2"
                  autocomplete="off"
                >
              </div>
            </div>
            <div class="modern-field modern-field-wide">
              <label>Remplacer le fichier</label>
              <label for="edit-version-file-input" class="upload-dropzone compact-upload">
                <span class="upload-icon"><i class="fas fa-cloud-upload-alt"></i></span>
                <span class="upload-content">
                  <strong>{{ editVersionForm.file ? editVersionForm.file.name : "Conserver le fichier actuel" }}</strong>
                  <small>{{ editVersionForm.file ? formatSize(editVersionForm.file.size) : "Optionnel" }}</small>
                </span>
                <span class="upload-action">Parcourir</span>
              </label>
              <input
                id="edit-version-file-input"
                ref="editVersionFile"
                type="file"
                class="documentation-file-input"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx"
                @change="setEditVersionFile"
              >
            </div>
            <div class="modern-field modern-field-full">
              <label for="edit-documentation-version-notes">Notes version</label>
              <textarea
                id="edit-documentation-version-notes"
                v-model="editVersionForm.notes"
                class="modern-textarea"
                rows="3"
              ></textarea>
            </div>
          </div>
        </form>
        <template slot="footer">
          <n-button
            type="primary"
            class="btn btn-sm btn-primary"
            :disabled="isSaving"
            @click.native="saveEditedVersion"
          >
            <span><i class="fas fa-save mr-2"></i>Enregistrer</span>
          </n-button>
          <n-button type="default" class="btn btn-sm btn-default" @click.native="closeEditVersionModal">
            <span>Annuler</span>
          </n-button>
        </template>
      </modal>

      <modal
        :show.sync="showQrModal"
        modal-classes="modal-dialog-centered qr-modal-dialog"
        body-classes="qr-modal-body"
        @close="closeQr"
      >
        <h5 slot="header" class="modal-title">
          QR code
        </h5>
        <div v-if="selectedQrDocumentation" class="qr-modal-shell">
          <div class="qr-header-card">
            <div class="qr-documentation-summary">
              <span>{{ selectedQrDocumentation.product_reference }}</span>
              <strong>{{ selectedQrDocumentation.title }}</strong>
              <small>
                {{ selectedQrDocumentation.product_name }}
                <template v-if="selectedQrDocumentation.current_version">
                  - v{{ selectedQrDocumentation.current_version.version }}
                </template>
              </small>
            </div>
            <span class="qr-ready-badge">
              <i class="fas fa-check-circle"></i>
              Pret
            </span>
          </div>
          <div class="qr-modal-grid">
            <div class="qr-preview-panel">
              <div class="qr-code-preview" v-html="qrSvg"></div>
            </div>
            <div class="qr-url-panel">
              <div class="qr-url-copy-row">
                <i class="fas fa-link qr-url-prefix"></i>
                <input
                  ref="qrUrlInput"
                  :value="selectedQrUrl"
                  readonly
                  class="form-control qr-url-input"
                  @focus="$event.target.select()"
                >
              </div>
              <div class="qr-action-grid">
                <button type="button" class="qr-action-card qr-action-primary" @click="copySelectedQrUrl">
                  <i class="fas fa-copy"></i>
                  <span>Copier</span>
                </button>
                <button type="button" class="qr-action-card" @click="openSelectedQrUrl">
                  <i class="fas fa-external-link-alt"></i>
                  <span>Ouvrir</span>
                </button>
                <button type="button" class="qr-action-card" @click="printQrPdf">
                  <i class="fas fa-print"></i>
                  <span>Imprimer</span>
                </button>
              </div>
              <div v-if="selectedQrDocumentation.has_qr_companion_pdf" class="qr-companion-meta">
                <i class="fas fa-file-pdf"></i>
                <span>
                  PDF compagnon enregistre
                  <template v-if="selectedQrDocumentation.qr_companion_pdf && selectedQrDocumentation.qr_companion_pdf.generated_at">
                    le {{ formatDate(selectedQrDocumentation.qr_companion_pdf.generated_at) }}
                  </template>
                </span>
              </div>
              <div class="qr-url-meta">
                <i class="fas fa-link"></i>
                <span>{{ selectedQrUrl }}</span>
              </div>
            </div>
          </div>
        </div>
        <template slot="footer">
          <n-button type="default" class="btn btn-sm btn-default" @click.native="closeQr">
            <span>Fermer</span>
          </n-button>
        </template>
      </modal>
    </div>
  </div>
</template>

<script>
import Modal from "@/components/Modal";
import Spinner from "@/components/Spinner/Circle";
import { createQrSvg } from "@/util/qr-code";
import { Option, Select } from "element-ui";
import Swal from "sweetalert2";

const DEFAULT_SHLINK_BASE_URL = process.env.VUE_APP_SHLINK_BASE_URL || "https://ec2e.link/";
const DEFAULT_DOCUMENTATION_LANGUAGE = "fr";
const DOCUMENTATION_LANGUAGES = [
  { value: "fr", label: "Francais", shortLabel: "FR" },
  { value: "en", label: "Anglais", shortLabel: "EN" },
  { value: "es", label: "Espagnol", shortLabel: "ES" },
];

export default {
  name: "ProductDocumentationList",
  components: {
    [Option.name]: Option,
    [Select.name]: Select,
    Modal,
    Spinner,
  },
  data() {
    return {
      apiBaseUrl: process.env.VUE_APP_API_BASE_URL,
      documentations: [],
      editVersionForm: this.emptyEditVersionForm(),
      expandedDocumentationId: null,
      form: this.emptyForm(),
      isLoaded: false,
      isEditingDocumentation: false,
      isSaving: false,
      languageOptions: DOCUMENTATION_LANGUAGES,
      qrSvg: "",
      searchText: "",
      selectedDocumentation: null,
      selectedQrDocumentation: null,
      selectedQrUrl: "",
      shlinkBaseUrl: DEFAULT_SHLINK_BASE_URL,
      selectedEditVersion: null,
      selectedEditVersionDocumentation: null,
      selectedLanguageFilter: "",
      selectedProductFilter: "",
      selectedVersionDocumentation: null,
      showCreateModal: false,
      showEditVersionModal: false,
      showQrModal: false,
      showVersionModal: false,
      versionForm: this.emptyVersionForm(),
    };
  },
  computed: {
    filteredDocumentations() {
      const search = this.searchText.trim().toLowerCase();
      let documentations = this.documentations;

      if (this.selectedProductFilter) {
        documentations = documentations.filter(
          documentation => this.productFilterValue(documentation) === this.selectedProductFilter
        );
      }

      if (this.selectedLanguageFilter) {
        documentations = documentations.filter(
          documentation => this.normalizedLanguage(documentation.language) === this.selectedLanguageFilter
        );
      }

      if (!search) {
        return documentations;
      }

      return documentations.filter(documentation => {
        const currentVersion = documentation.current_version || {};

        return [
          documentation.product_reference,
          documentation.product_name,
          documentation.title,
          documentation.description,
          documentation.accessibility_url,
          this.languageLabel(documentation.language),
          currentVersion.version,
          currentVersion.original_name,
        ]
          .filter(Boolean)
          .some(value => String(value).toLowerCase().includes(search));
      });
    },
    productFilterOptions() {
      const products = {};

      this.documentations.forEach(documentation => {
        const value = this.productFilterValue(documentation);
        if (value === "::") {
          return;
        }

        if (!products[value]) {
          products[value] = {
            count: 0,
            name: String(documentation.product_name || "").trim() || "Produit sans libelle",
            reference: String(documentation.product_reference || "").trim() || "Reference non renseignee",
            value,
          };
        }

        products[value].count += 1;
      });

      return Object.keys(products)
        .map(value => {
          const product = products[value];
          return {
            ...product,
            label: `${product.name} - ${product.reference} (${product.count} doc${product.count > 1 ? "s" : ""})`,
          };
        })
        .sort((left, right) => left.label.localeCompare(right.label, "fr", { sensitivity: "base" }));
    },
    languageFilterOptions() {
      return [
        { value: "", label: "Toutes les langues", shortLabel: "Toutes" },
        ...this.languageOptions,
      ];
    },
    documentationQrPreviews() {
      return this.documentations.reduce((previews, documentation) => {
        if (documentation.has_illustration) {
          return previews;
        }

        const url = this.qrUrl(documentation);
        if (!url) {
          return previews;
        }

        try {
          previews[documentation.id] = createQrSvg(url, 3, 3);
        } catch (error) {
          previews[documentation.id] = "";
        }

        return previews;
      }, {});
    },
    illustrationUploadLabel() {
      if (
        this.isEditingDocumentation
        && this.selectedDocumentation
        && this.selectedDocumentation.has_illustration
      ) {
        return "Remplacer l'image produit";
      }

      return "Ajouter une image produit";
    },
    companionPdfUploadLabel() {
      if (
        this.isEditingDocumentation
        && this.selectedDocumentation
        && this.selectedDocumentation.has_qr_companion_pdf
      ) {
        return "Remplacer le document PDF";
      }

      return "Ajouter un document PDF";
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    emptyForm() {
      return {
        product_reference: "",
        product_name: "",
        title: "",
        language: DEFAULT_DOCUMENTATION_LANGUAGE,
        version: "1.0",
        description: "",
        accessibility_url: this.defaultQrDisplayUrl(),
        notes: "",
        file: null,
        illustration: null,
        qr_companion_pdf: null,
      };
    },
    normalizedLanguage(value) {
      const language = String(value || "").trim().toLowerCase();

      return this.languageOptions.some(option => option.value === language)
        ? language
        : DEFAULT_DOCUMENTATION_LANGUAGE;
    },
    languageOption(value) {
      const language = this.normalizedLanguage(value);

      return this.languageOptions.find(option => option.value === language)
        || this.languageOptions[0];
    },
    languageFlagClass(value) {
      return `language-flag-${this.normalizedLanguage(value)}`;
    },
    languageLabel(value) {
      return this.languageOption(value).label;
    },
    languageShortLabel(value) {
      return this.languageOption(value).shortLabel;
    },
    emptyVersionForm() {
      return {
        documentationId: null,
        version: "",
        file: null,
        notes: "",
      };
    },
    emptyEditVersionForm() {
      return {
        documentationId: null,
        versionId: null,
        version: "",
        file: null,
        notes: "",
      };
    },
    productFilterValue(documentation) {
      return [
        this.normalizedProductFilterPart(documentation.product_reference),
        this.normalizedProductFilterPart(documentation.product_name),
      ].join("::");
    },
    normalizedProductFilterPart(value) {
      return String(value || "").trim().toLowerCase();
    },
    fetchData() {
      this.axios
        .get(`${this.apiBaseUrl}product-documentations`)
        .then(response => {
          this.documentations = response.data;
          if (
            this.selectedProductFilter
            && !this.documentations.some(
              documentation => this.productFilterValue(documentation) === this.selectedProductFilter
            )
          ) {
            this.selectedProductFilter = "";
          }
          if (
            this.selectedLanguageFilter
            && !this.documentations.some(
              documentation => this.normalizedLanguage(documentation.language) === this.selectedLanguageFilter
            )
          ) {
            this.selectedLanguageFilter = "";
          }
          this.isLoaded = true;
        })
        .catch(() => {
          this.isLoaded = true;
          this.notifyError("Impossible de charger les documentations produits.");
        });
    },
    replaceDocumentation(updatedDocumentation) {
      const index = this.documentations.findIndex(
        documentation => documentation.id === updatedDocumentation.id
      );

      if (index === -1) {
        this.documentations.push(updatedDocumentation);
        return;
      }

      this.$set(this.documentations, index, updatedDocumentation);
    },
    openCreateModal() {
      this.form = this.emptyForm();
      this.isEditingDocumentation = false;
      this.selectedDocumentation = null;
      this.showCreateModal = true;
    },
    openEditDocumentationModal(documentation) {
      this.selectedDocumentation = documentation;
      this.isEditingDocumentation = true;
      this.form = {
        product_reference: documentation.product_reference || "",
        product_name: documentation.product_name || "",
        title: documentation.title || "",
        language: this.normalizedLanguage(documentation.language),
        version: "1.0",
        description: documentation.description || "",
        accessibility_url: documentation.accessibility_url || this.defaultQrDisplayUrl(),
        notes: "",
        file: null,
        illustration: null,
        qr_companion_pdf: null,
      };
      this.showCreateModal = true;
    },
    closeCreateModal() {
      this.showCreateModal = false;
      this.isEditingDocumentation = false;
      this.selectedDocumentation = null;
      this.form = this.emptyForm();
      this.clearFileInput("documentationFile");
      this.clearFileInput("documentationIllustration");
      this.clearFileInput("documentationCompanionPdf");
    },
    openVersionModal(documentation) {
      this.selectedVersionDocumentation = documentation;
      this.versionForm = this.emptyVersionForm();
      this.versionForm.documentationId = documentation.id;
      this.showVersionModal = true;
    },
    closeVersionModal() {
      this.showVersionModal = false;
      this.selectedVersionDocumentation = null;
      this.versionForm = this.emptyVersionForm();
      this.clearFileInput("versionFile");
    },
    openEditVersionModal(documentation, version) {
      this.selectedEditVersionDocumentation = documentation;
      this.selectedEditVersion = version;
      this.editVersionForm = {
        documentationId: documentation.id,
        versionId: version.id,
        version: version.version,
        file: null,
        notes: version.notes || "",
      };
      this.showEditVersionModal = true;
    },
    closeEditVersionModal() {
      this.showEditVersionModal = false;
      this.selectedEditVersionDocumentation = null;
      this.selectedEditVersion = null;
      this.editVersionForm = this.emptyEditVersionForm();
      this.clearFileInput("editVersionFile");
    },
    setDocumentationFile(event) {
      this.form.file = event.target.files[0] || null;
    },
    setDocumentationIllustration(event) {
      this.form.illustration = event.target.files[0] || null;
    },
    setDocumentationCompanionPdf(event) {
      this.form.qr_companion_pdf = event.target.files[0] || null;
    },
    setVersionFile(event) {
      this.versionForm.file = event.target.files[0] || null;
    },
    setEditVersionFile(event) {
      this.editVersionForm.file = event.target.files[0] || null;
    },
    saveDocumentation() {
      if (this.isSaving) {
        return;
      }

      if (this.isEditingDocumentation && !this.selectedDocumentation) {
        return;
      }

      if (!this.validateDocumentationForm()) {
        return;
      }

      this.isSaving = true;
      const request = this.isEditingDocumentation
        ? this.saveEditedDocumentation()
        : this.saveNewDocumentation();

      request
        .then(() => {
          this.$notify({
            message: "La documentation a bien été enregistrée",
            type: "success",
            verticalAlign: "bottom",
          });
          this.closeCreateModal();
          this.fetchData();
        })
        .catch(error => {
          this.notifyRequestError(
            error,
            "Impossible d'enregistrer la documentation."
          );
        })
        .then(() => {
          this.isSaving = false;
        });
    },
    saveNewDocumentation() {
      const formData = new FormData();
      formData.append("product_reference", this.form.product_reference);
      formData.append("product_name", this.form.product_name);
      formData.append("title", this.form.title);
      formData.append("language", this.normalizedLanguage(this.form.language));
      formData.append("version", this.normalizeVersion(this.form.version));
      formData.append("description", this.form.description || "");
      formData.append("accessibility_url", this.qrDisplayUrlForPayload());
      formData.append("notes", this.form.notes || "");
      formData.append("file", this.form.file);
      if (this.form.illustration) {
        formData.append("illustration", this.form.illustration);
      }
      if (this.form.qr_companion_pdf) {
        formData.append("qr_companion_pdf", this.form.qr_companion_pdf);
      }

      return this.axios.post(`${this.apiBaseUrl}product-documentations`, formData);
    },
    saveEditedDocumentation() {
      return this.axios
        .put(
          `${this.apiBaseUrl}product-documentations/${this.selectedDocumentation.id}`,
          {
            product_reference: this.form.product_reference,
            product_name: this.form.product_name,
            title: this.form.title,
            language: this.normalizedLanguage(this.form.language),
            description: this.form.description || "",
            accessibility_url: this.qrDisplayUrlForPayload(),
          }
        )
        .then(response => {
          if (!this.form.illustration) {
            return response;
          }

          return this.uploadDocumentationIllustration();
        })
        .then(response => {
          if (!this.form.qr_companion_pdf) {
            return response;
          }

          return this.uploadDocumentationCompanionPdf();
        });
    },
    uploadDocumentationIllustration() {
      const formData = new FormData();
      formData.append("illustration", this.form.illustration);

      return this.axios.post(
        `${this.apiBaseUrl}product-documentations/${this.selectedDocumentation.id}/illustration`,
        formData
      );
    },
    uploadDocumentationCompanionPdf() {
      const formData = new FormData();
      formData.append("file", this.form.qr_companion_pdf);

      return this.axios.post(
        `${this.apiBaseUrl}product-documentations/${this.selectedDocumentation.id}/qr-companion-pdf`,
        formData
      );
    },
    confirmDeleteDocumentationIllustration() {
      if (this.isSaving || !this.selectedDocumentation || !this.selectedDocumentation.has_illustration) {
        return;
      }

      Swal.fire({
        title: "Supprimer l'illustration ?",
        text: "L'image produit associee a cette documentation sera supprimee.",
        type: "warning",
        showCancelButton: true,
        confirmButtonClass: "btn btn-danger btn-fill",
        cancelButtonClass: "btn btn-secondary btn-fill",
        cancelButtonText: "Annuler",
        confirmButtonText: "Supprimer",
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          this.deleteDocumentationIllustration();
        }
      });
    },
    deleteDocumentationIllustration() {
      if (this.isSaving || !this.selectedDocumentation) {
        return;
      }

      this.isSaving = true;
      this.axios
        .delete(`${this.apiBaseUrl}product-documentations/${this.selectedDocumentation.id}/illustration`)
        .then(response => {
          this.applyEditedDocumentationResponse(response.data);
          this.form.illustration = null;
          this.clearFileInput("documentationIllustration");
          this.$notify({
            message: "L'illustration a bien ete supprimee",
            type: "success",
            verticalAlign: "bottom",
          });
        })
        .catch(error => {
          this.notifyRequestError(
            error,
            "Impossible de supprimer l'illustration."
          );
        })
        .then(() => {
          this.isSaving = false;
        });
    },
    confirmDeleteDocumentationCompanionPdf() {
      if (this.isSaving || !this.selectedDocumentation || !this.selectedDocumentation.has_qr_companion_pdf) {
        return;
      }

      Swal.fire({
        title: "Supprimer le document d'accompagnement ?",
        text: "Le PDF associe au QR code sera supprime.",
        type: "warning",
        showCancelButton: true,
        confirmButtonClass: "btn btn-danger btn-fill",
        cancelButtonClass: "btn btn-secondary btn-fill",
        cancelButtonText: "Annuler",
        confirmButtonText: "Supprimer",
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          this.deleteDocumentationCompanionPdf();
        }
      });
    },
    deleteDocumentationCompanionPdf() {
      if (this.isSaving || !this.selectedDocumentation) {
        return;
      }

      this.isSaving = true;
      this.axios
        .delete(`${this.apiBaseUrl}product-documentations/${this.selectedDocumentation.id}/qr-companion-pdf`)
        .then(response => {
          this.applyEditedDocumentationResponse(response.data);
          this.form.qr_companion_pdf = null;
          this.clearFileInput("documentationCompanionPdf");
          this.$notify({
            message: "Le document d'accompagnement a bien ete supprime",
            type: "success",
            verticalAlign: "bottom",
          });
        })
        .catch(error => {
          this.notifyRequestError(
            error,
            "Impossible de supprimer le document d'accompagnement."
          );
        })
        .then(() => {
          this.isSaving = false;
        });
    },
    applyEditedDocumentationResponse(documentation) {
      this.replaceDocumentation(documentation);
      this.selectedDocumentation = documentation;
    },
    saveVersion() {
      if (this.isSaving || !this.selectedVersionDocumentation) {
        return;
      }

      if (!this.validateVersionForm()) {
        return;
      }

      const formData = new FormData();
      formData.append("version", this.normalizeVersion(this.versionForm.version));
      formData.append("file", this.versionForm.file);
      formData.append("notes", this.versionForm.notes || "");

      this.isSaving = true;
      this.axios
        .post(
          `${this.apiBaseUrl}product-documentations/${this.selectedVersionDocumentation.id}/versions`,
          formData
        )
        .then(() => {
          this.$notify({
            message: "La nouvelle version a bien été ajoutée",
            type: "success",
            verticalAlign: "bottom",
          });
          this.closeVersionModal();
          this.fetchData();
        })
        .catch(error => {
          this.notifyRequestError(
            error,
            "Impossible d'ajouter la nouvelle version."
          );
        })
        .then(() => {
          this.isSaving = false;
        });
    },
    saveEditedVersion() {
      if (
        this.isSaving ||
        !this.selectedEditVersionDocumentation ||
        !this.selectedEditVersion
      ) {
        return;
      }

      if (!this.validateEditVersionForm()) {
        return;
      }

      const formData = new FormData();
      formData.append("version", this.normalizeVersion(this.editVersionForm.version));
      formData.append("notes", this.editVersionForm.notes || "");
      if (this.editVersionForm.file) {
        formData.append("file", this.editVersionForm.file);
      }

      this.isSaving = true;
      this.axios
        .post(
          `${this.apiBaseUrl}product-documentations/${this.selectedEditVersionDocumentation.id}/versions/${this.selectedEditVersion.id}`,
          formData
        )
        .then(() => {
          this.$notify({
            message: "La version a bien été modifiée",
            type: "success",
            verticalAlign: "bottom",
          });
          this.closeEditVersionModal();
          this.fetchData();
        })
        .catch(error => {
          this.notifyRequestError(
            error,
            "Impossible de modifier cette version."
          );
        })
        .then(() => {
          this.isSaving = false;
        });
    },
    confirmDeleteVersion(documentation, version) {
      Swal.fire({
        title: "Supprimer cette version ?",
        text: "Le fichier associé à cette version sera supprimé.",
        type: "warning",
        showCancelButton: true,
        confirmButtonClass: "btn btn-danger btn-fill",
        cancelButtonClass: "btn btn-secondary btn-fill",
        cancelButtonText: "Annuler",
        confirmButtonText: "Supprimer",
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          this.deleteVersion(documentation, version);
        }
      });
    },
    deleteVersion(documentation, version) {
      if (this.isSaving) {
        return;
      }

      this.isSaving = true;
      this.axios
        .delete(
          `${this.apiBaseUrl}product-documentations/${documentation.id}/versions/${version.id}`
        )
        .then(() => {
          this.$notify({
            message: "La version a bien été supprimée",
            type: "success",
            verticalAlign: "bottom",
          });
          this.fetchData();
        })
        .catch(error => {
          this.notifyRequestError(
            error,
            "Impossible de supprimer cette version."
          );
        })
        .then(() => {
          this.isSaving = false;
        });
    },
    validateDocumentationForm() {
      if (
        !this.form.product_reference ||
        !this.form.product_name ||
        !this.form.title ||
        (!this.isEditingDocumentation && !this.normalizeVersion(this.form.version))
      ) {
        this.notifyError("La référence, le produit, le titre et la version sont obligatoires.");
        return false;
      }

      if (!this.isEditingDocumentation && !this.isValidVersion(this.form.version)) {
        this.notifyError("La version doit être un nombre entier ou décimal (ex : 1.2).");
        return false;
      }

      if (!this.isEditingDocumentation && !this.form.file) {
        this.notifyError("Le fichier est obligatoire.");
        return false;
      }

      if (this.normalizedLanguage(this.form.language) !== this.form.language) {
        this.notifyError("La langue selectionnee est invalide.");
        return false;
      }

      if (this.qrDisplayUrlForPayload() && !this.isValidQrDisplayUrl(this.form.accessibility_url)) {
        this.notifyError(`L'URL affichee dans le QR doit commencer par ${this.normalizedShlinkBaseUrl()}`);
        return false;
      }

      return true;
    },
    validateVersionForm() {
      if (!this.normalizeVersion(this.versionForm.version)) {
        this.notifyError("La version est obligatoire.");
        return false;
      }

      if (!this.isValidVersion(this.versionForm.version)) {
        this.notifyError("La version doit être un nombre entier ou décimal (ex : 1.2).");
        return false;
      }

      if (!this.versionForm.file) {
        this.notifyError("Le fichier est obligatoire.");
        return false;
      }

      return true;
    },
    validateEditVersionForm() {
      if (!this.normalizeVersion(this.editVersionForm.version)) {
        this.notifyError("La version est obligatoire.");
        return false;
      }

      if (!this.isValidVersion(this.editVersionForm.version)) {
        this.notifyError("La version doit être un nombre entier ou décimal (ex : 1.2).");
        return false;
      }

      return true;
    },
    normalizeVersion(value) {
      return String(value || "").trim();
    },
    normalizeUrl(value) {
      return String(value || "").trim();
    },
    normalizedShlinkBaseUrl() {
      return `${String(this.shlinkBaseUrl || DEFAULT_SHLINK_BASE_URL).replace(/\/+$/, "")}/`;
    },
    defaultQrDisplayUrl() {
      return `${String(DEFAULT_SHLINK_BASE_URL).replace(/\/+$/, "")}/`;
    },
    qrDisplayUrlForPayload() {
      const url = this.normalizeUrl(this.form.accessibility_url);

      return url === this.normalizedShlinkBaseUrl() ? "" : url;
    },
    isValidVersion(value) {
      return /^\d+(\.\d+)?$/.test(this.normalizeVersion(value));
    },
    isValidUrl(value) {
      try {
        const url = new URL(this.normalizeUrl(value));

        return ["http:", "https:"].includes(url.protocol);
      } catch (error) {
        return false;
      }
    },
    isValidQrDisplayUrl(value) {
      return this.isValidUrl(value)
        && this.normalizeUrl(value).indexOf(this.normalizedShlinkBaseUrl()) === 0
        && this.normalizeUrl(value).length > this.normalizedShlinkBaseUrl().length;
    },
    toggleHistory(documentation) {
      this.expandedDocumentationId = this.expandedDocumentationId === documentation.id
        ? null
        : documentation.id;
    },
    openLatestDocumentation(documentation) {
      if (!documentation.current_version) {
        this.notifyError("Aucune version disponible pour cette documentation.");
        return;
      }

      window.open(this.publicUrl(documentation), "_blank", "noopener");
    },
    downloadVersion(documentation, version) {
      this.axios
        .get(
          `${this.apiBaseUrl}product-documentations/${documentation.id}/versions/${version.id}/download`,
          { responseType: "blob" }
        )
        .then(response => {
          const contentType = version.mime_type || response.headers["content-type"];
          const blob = new Blob([response.data], { type: contentType });
          this.downloadBlob(blob, version.original_name);
        })
        .catch(() => {
          this.notifyError("Impossible de télécharger cette version.");
        });
    },
    downloadQrCompanionPdf(documentation) {
      if (!documentation.has_qr_companion_pdf) {
        this.notifyError("Aucun PDF compagnon QR n'est disponible pour cette documentation.");
        return;
      }

      this.axios
        .get(
          `${this.apiBaseUrl}product-documentations/${documentation.id}/qr-companion-pdf`,
          { responseType: "blob" }
        )
        .then(response => {
          const companion = documentation.qr_companion_pdf || {};
          const contentType = companion.mime_type || response.headers["content-type"] || "application/pdf";
          const fileName = companion.original_name || this.qrCompanionPdfFileName(documentation);
          const blob = new Blob([response.data], { type: contentType });

          this.downloadBlob(blob, fileName);
        })
        .catch(() => {
          this.notifyError("Impossible de telecharger le PDF compagnon QR.");
        });
    },
    openQr(documentation) {
      if (!documentation.current_version) {
        this.notifyError("Aucune version disponible pour cette documentation.");
        return;
      }

      const url = this.qrUrl(documentation);
      if (!url) {
        this.notifyError("Aucune URL QR n'est disponible pour cette documentation.");
        return;
      }

      try {
        this.qrSvg = createQrSvg(url);
        this.selectedQrDocumentation = documentation;
        this.selectedQrUrl = url;
        this.showQrModal = true;
      } catch (error) {
        this.notifyError(error.message);
      }
    },
    closeQr() {
      this.showQrModal = false;
      this.selectedQrDocumentation = null;
      this.selectedQrUrl = "";
      this.qrSvg = "";
    },
    openSelectedQrUrl() {
      if (!this.selectedQrUrl) {
        return;
      }

      window.open(this.selectedQrUrl, "_blank", "noopener");
    },
    copySelectedQrUrl() {
      if (!this.selectedQrUrl) {
        return;
      }

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(this.selectedQrUrl)
          .then(() => {
            this.$notify({
              message: "URL du QR code copiee",
              type: "success",
              verticalAlign: "bottom",
            });
          })
          .catch(() => {
            this.copySelectedQrUrlFallback();
          });
        return;
      }

      this.copySelectedQrUrlFallback();
    },
    copySelectedQrUrlFallback() {
      const input = Array.isArray(this.$refs.qrUrlInput)
        ? this.$refs.qrUrlInput[0]
        : this.$refs.qrUrlInput;

      if (!input) {
        return;
      }

      input.focus();
      input.select();
      document.execCommand("copy");
      this.$notify({
        message: "URL du QR code copiee",
        type: "success",
        verticalAlign: "bottom",
      });
    },
    async printQrPdf() {
      if (!this.qrSvg || !this.selectedQrDocumentation) {
        return;
      }

      try {
        const { blob, fileName } = await this.buildSelectedQrPdfBlob();
        const pdfBlob = this.selectedQrDocumentation.has_qr_companion_pdf
          ? await this.mergedQrCompanionPdfBlob(blob, fileName)
          : blob;

        this.printPdfBlob(pdfBlob);
      } catch (error) {
        this.notifyRequestError(
          error,
          "Impossible de preparer l'impression du QR code."
        );
      }
    },
    async mergedQrCompanionPdfBlob(qrPdfBlob, fallbackFileName) {
      const formData = new FormData();
      formData.append("file", qrPdfBlob, fallbackFileName);

      const response = await this.axios.post(
        `${this.apiBaseUrl}product-documentations/${this.selectedQrDocumentation.id}/qr-companion-merged-pdf`,
        formData,
        { responseType: "blob" }
      );
      const contentType = response.headers["content-type"] || "application/pdf";

      return new Blob([response.data], { type: contentType });
    },
    async buildSelectedQrPdfBlob() {
      const qrImage = await this.qrSvgToJpegBytes(this.qrSvg, 720);
      const productImage = await this.documentationIllustrationToJpegBytes(this.selectedQrDocumentation);
      const pdfBytes = this.buildQrDocumentationPdf(qrImage, productImage);

      return {
        blob: new Blob([pdfBytes], { type: "application/pdf" }),
        fileName: this.qrCompanionPdfFileName(this.selectedQrDocumentation),
      };
    },
    qrSvgToJpegBytes(svg, size) {
      return new Promise((resolve, reject) => {
        const image = new Image();
        const svgBlob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
        const svgUrl = URL.createObjectURL(svgBlob);

        image.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = size;
          canvas.height = size;

          const context = canvas.getContext("2d");
          context.fillStyle = "#ffffff";
          context.fillRect(0, 0, size, size);
          context.drawImage(image, 0, 0, size, size);

          URL.revokeObjectURL(svgUrl);
          resolve({
            bytes: this.dataUrlToBytes(canvas.toDataURL("image/jpeg", 0.96)),
            width: size,
            height: size,
          });
        };

        image.onerror = () => {
          URL.revokeObjectURL(svgUrl);
          reject(new Error("QR image load failed."));
        };

        image.src = svgUrl;
      });
    },
    async documentationIllustrationToJpegBytes(documentation) {
      if (!documentation || !documentation.has_illustration) {
        return null;
      }

      try {
        const response = await this.axios.get(
          this.illustrationUrl(documentation),
          { responseType: "blob" }
        );

        return await this.imageBlobToJpegBytes(response.data, 720);
      } catch (error) {
        return null;
      }
    },
    imageBlobToJpegBytes(blob, maxSize) {
      return new Promise((resolve, reject) => {
        const image = new Image();
        const imageUrl = URL.createObjectURL(blob);

        image.onload = () => {
          const sourceWidth = image.naturalWidth || image.width;
          const sourceHeight = image.naturalHeight || image.height;

          if (!sourceWidth || !sourceHeight) {
            URL.revokeObjectURL(imageUrl);
            reject(new Error("Product image load failed."));
            return;
          }

          const ratio = Math.min(maxSize / sourceWidth, maxSize / sourceHeight, 1);
          const canvas = document.createElement("canvas");
          canvas.width = Math.max(1, Math.round(sourceWidth * ratio));
          canvas.height = Math.max(1, Math.round(sourceHeight * ratio));

          const context = canvas.getContext("2d");
          context.fillStyle = "#ffffff";
          context.fillRect(0, 0, canvas.width, canvas.height);
          context.drawImage(image, 0, 0, canvas.width, canvas.height);

          URL.revokeObjectURL(imageUrl);
          resolve({
            bytes: this.dataUrlToBytes(canvas.toDataURL("image/jpeg", 0.92)),
            width: canvas.width,
            height: canvas.height,
          });
        };

        image.onerror = () => {
          URL.revokeObjectURL(imageUrl);
          reject(new Error("Product image load failed."));
        };

        image.src = imageUrl;
      });
    },
    buildQrDocumentationPdf(qrImage, productImage = null) {
      const documentation = this.selectedQrDocumentation;
      const version = documentation.current_version || {};
      const pageWidth = 841.89;
      const pageHeight = 595.28;
      const content = [];
      const today = new Date().toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
      const primaryColor = "#257ee2";
      const primaryDarkColor = "#1f67b8";
      const primarySoftColor = "#eaf3ff";
      const detailFields = [
        ["Fichier", version.original_name || "-"],
        ["Taille", version.size ? this.formatSize(version.size) : "-"],
        ["Mise a jour", this.formatDate(documentation.updated_at) || "-"],
      ];

      const color = hex => {
        const clean = hex.replace("#", "");
        const red = parseInt(clean.slice(0, 2), 16) / 255;
        const green = parseInt(clean.slice(2, 4), 16) / 255;
        const blue = parseInt(clean.slice(4, 6), 16) / 255;

        return `${red.toFixed(3)} ${green.toFixed(3)} ${blue.toFixed(3)}`;
      };
      const pdfY = (top, height = 0) => (pageHeight - top - height).toFixed(2);
      const number = value => Number(value).toFixed(2);
      const drawRect = (x, top, width, height, fill) => {
        content.push(`${color(fill)} rg ${number(x)} ${pdfY(top, height)} ${number(width)} ${number(height)} re f\n`);
      };
      const drawStrokeRect = (x, top, width, height, stroke) => {
        content.push(`q 1 w ${color(stroke)} RG ${number(x)} ${pdfY(top, height)} ${number(width)} ${number(height)} re S Q\n`);
      };
      const drawLine = (x1, top1, x2, top2, stroke) => {
        content.push(`q 1 w ${color(stroke)} RG ${number(x1)} ${pdfY(top1)} m ${number(x2)} ${pdfY(top2)} l S Q\n`);
      };
      const drawText = (text, x, top, size, fill = "#1f2937", font = "F1") => {
        content.push(
          `BT /${font} ${number(size)} Tf ${color(fill)} rg 1 0 0 1 ${number(x)} ${pdfY(top)} Tm <${this.pdfTextHex(text)}> Tj ET\n`
        );
      };
      const drawWrappedText = (text, x, top, maxWidth, size, fill = "#1f2937", font = "F1", maxLines = null) => {
        const lines = this.wrapPdfText(text, maxWidth, size, maxLines);
        lines.forEach((line, index) => {
          drawText(line, x, top + index * (size + 4), size, fill, font);
        });

        return lines.length * (size + 4);
      };
      const drawImage = (name, x, top, width, height) => {
        content.push(`q ${number(width)} 0 0 ${number(height)} ${number(x)} ${pdfY(top, height)} cm /${name} Do Q\n`);
      };
      const drawImageFit = (name, image, x, top, width, height) => {
        const ratio = Math.min(width / image.width, height / image.height);
        const imageWidth = image.width * ratio;
        const imageHeight = image.height * ratio;
        drawImage(
          name,
          x + (width - imageWidth) / 2,
          top + (height - imageHeight) / 2,
          imageWidth,
          imageHeight
        );
      };
      const drawInfoRow = (label, value, x, top, width, index) => {
        if (index % 2 === 0) {
          drawRect(x, top - 3, width, 25, "#f8fafc");
        }

        drawText(label.toUpperCase(), x + 10, top + 12, 7.2, "#8a94a6", "F2");
        drawWrappedText(value, x + 94, top + 7, width - 108, 9.4, "#111827", "F1", 1);
      };
      const imageObject = image => [
        `<< /Type /XObject /Subtype /Image /Width ${image.width} /Height ${image.height} `,
        `/ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${image.bytes.length} >>\nstream\n`,
        image.bytes,
        "\nendstream",
      ];
      const xObjectResources = productImage
        ? "/Im1 6 0 R /Im2 7 0 R"
        : "/Im1 6 0 R";
      const contentObjectNumber = productImage ? 8 : 7;

      drawRect(0, 0, pageWidth, pageHeight, "#f5f7fb");
      drawRect(0, 0, pageWidth, 76, primaryColor);
      drawRect(0, 76, pageWidth, 5, primaryDarkColor);
      drawText("Fiche QR documentation", 36, 30, 19, "#ffffff", "F2");
      drawText(`Generee le ${today}`, 36, 54, 9, primarySoftColor, "F1");

      drawRect(662, 24, 132, 30, "#ffffff");
      drawWrappedText(documentation.product_reference || "Reference", 674, 44, 108, 11, primaryColor, "F2", 1);

      drawRect(28, 100, 786, 422, "#ffffff");
      drawStrokeRect(28, 100, 786, 422, "#dfe5ee");
      drawRect(28, 100, 786, 8, primarySoftColor);

      drawWrappedText(documentation.title || "Documentation", 54, 136, 500, 19, "#111827", "F2", 2);
      drawWrappedText(documentation.product_name || "Produit non renseigne", 54, 164, 430, 11, "#667085", "F1", 1);

      drawRect(586, 128, 72, 26, "#f8fafc");
      drawStrokeRect(586, 128, 72, 26, "#e4e9f1");
      drawText("VERSION", 598, 144, 7, "#8a94a6", "F2");
      drawText(version.version ? `v${version.version}` : "-", 636, 144, 10, "#111827", "F2");
      drawRect(670, 128, 72, 26, "#f8fafc");
      drawStrokeRect(670, 128, 72, 26, "#e4e9f1");
      drawText("LANGUE", 682, 144, 7, "#8a94a6", "F2");
      drawText(this.languageShortLabel(documentation.language), 722, 144, 10, "#111827", "F2");

      drawText("Image produit", 58, 190, 12, "#111827", "F2");
      drawRect(58, 208, 206, 206, "#ffffff");
      drawStrokeRect(58, 208, 206, 206, "#e3e8ef");
      if (productImage) {
        drawImageFit("Im2", productImage, 74, 224, 174, 174);
      } else {
        drawText("Aucune image produit", 104, 314, 10, "#8a94a6", "F1");
      }

      drawText("QR code d'acces", 334, 190, 12, "#111827", "F2");
      drawRect(296, 208, 206, 206, "#ffffff");
      drawStrokeRect(296, 208, 206, 206, "#e3e8ef");
      drawImage("Im1", 312, 224, 174, 174);
      drawWrappedText("Scannez ce code pour ouvrir la documentation.", 300, 434, 198, 9, "#667085", "F1", 2);

      drawText("Document associe", 548, 205, 12, "#111827", "F2");
      drawStrokeRect(548, 222, 220, 92, "#e4e9f1");
      detailFields.forEach(([label, value], index) => {
        drawInfoRow(label, value, 548, 237 + index * 25, 220, index);
      });

      drawRect(548, 334, 220, 80, primarySoftColor);
      drawStrokeRect(548, 334, 220, 80, "#bfdbfe");
      drawText("Lien associe au QR code", 564, 358, 10.5, "#1d4ed8", "F2");
      drawWrappedText(this.selectedQrUrl, 564, 380, 188, 8.4, "#1e3a8a", "F1", 3);

      drawText("Description", 58, 452, 11.5, "#111827", "F2");
      drawLine(58, 466, 768, 466, "#e5e7eb");
      drawWrappedText(
        documentation.description || "Aucune description renseignee.",
        58,
        488,
        version.notes ? 420 : 700,
        9.5,
        "#4b5563",
        "F1",
        2
      );

      if (version.notes) {
        drawText("Notes de version", 526, 486, 9.5, "#111827", "F2");
        drawWrappedText(version.notes, 526, 506, 230, 8.5, "#4b5563", "F1", 1);
      }

      const contentStream = content.join("");
      const objects = [
        ["<< /Type /Catalog /Pages 2 0 R >>"],
        ["<< /Type /Pages /Kids [3 0 R] /Count 1 >>"],
        [
          `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${number(pageWidth)} ${number(pageHeight)}] `,
          `/Resources << /Font << /F1 4 0 R /F2 5 0 R >> /XObject << ${xObjectResources} >> >> `,
          `/Contents ${contentObjectNumber} 0 R >>`,
        ],
        ["<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>"],
        ["<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>"],
        imageObject(qrImage),
      ];

      if (productImage) {
        objects.push(imageObject(productImage));
      }

      objects.push([`<< /Length ${contentStream.length} >>\nstream\n${contentStream}\nendstream`]);

      return this.assemblePdf(objects);
    },
    wrapPdfText(value, maxWidth, fontSize, maxLines = null) {
      const maxCharacters = Math.max(8, Math.floor(maxWidth / (fontSize * 0.52)));
      const source = String(value || "").replace(/\s+/g, " ").trim();
      const words = [];

      source.split(" ").forEach(word => {
        if (word.length <= maxCharacters) {
          words.push(word);
          return;
        }

        for (let index = 0; index < word.length; index += maxCharacters) {
          words.push(word.slice(index, index + maxCharacters));
        }
      });

      const lines = [];
      words.forEach(word => {
        const current = lines[lines.length - 1] || "";
        const candidate = current ? `${current} ${word}` : word;

        if (candidate.length <= maxCharacters) {
          if (lines.length === 0) {
            lines.push(candidate);
          } else {
            lines[lines.length - 1] = candidate;
          }
        } else {
          lines.push(word);
        }
      });

      if (maxLines && lines.length > maxLines) {
        const limited = lines.slice(0, maxLines);
        const last = limited[limited.length - 1];
        limited[limited.length - 1] = `${last.slice(0, Math.max(0, maxCharacters - 3))}...`;
        return limited;
      }

      return lines.length ? lines : [""];
    },
    pdfTextHex(value) {
      const text = this.pdfWinAnsiText(value);
      let hex = "";

      for (let index = 0; index < text.length; index += 1) {
        const code = text.charCodeAt(index);
        hex += (code <= 255 ? code : 63).toString(16).padStart(2, "0").toUpperCase();
      }

      return hex;
    },
    pdfWinAnsiText(value) {
      const replacements = {
        "€": "EUR",
        "œ": "oe",
        "Œ": "OE",
        "–": "-",
        "—": "-",
        "’": "'",
        "‘": "'",
        "“": "\"",
        "”": "\"",
        "…": "...",
      };

      return String(value || "")
        .split("")
        .map(character => replacements[character] || character)
        .join("");
    },
    assemblePdf(objects) {
      const chunks = [];
      const offsets = [];
      let offset = 0;
      const push = chunk => {
        const bytes = typeof chunk === "string" ? this.stringToPdfBytes(chunk) : chunk;
        chunks.push(bytes);
        offset += bytes.length;
      };

      push("%PDF-1.4\n%\xE2\xE3\xCF\xD3\n");
      objects.forEach((objectChunks, index) => {
        offsets.push(offset);
        push(`${index + 1} 0 obj\n`);
        objectChunks.forEach(push);
        push("\nendobj\n");
      });

      const xrefOffset = offset;
      push([
        `xref\n0 ${objects.length + 1}\n`,
        "0000000000 65535 f \n",
        offsets.map(value => `${String(value).padStart(10, "0")} 00000 n \n`).join(""),
        `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\n`,
        `startxref\n${xrefOffset}\n%%EOF`,
      ].join(""));

      const pdfBytes = new Uint8Array(offset);
      let cursor = 0;
      chunks.forEach(chunk => {
        pdfBytes.set(chunk, cursor);
        cursor += chunk.length;
      });

      return pdfBytes;
    },
    stringToPdfBytes(value) {
      const bytes = new Uint8Array(value.length);

      for (let index = 0; index < value.length; index += 1) {
        bytes[index] = value.charCodeAt(index) & 0xff;
      }

      return bytes;
    },
    dataUrlToBytes(dataUrl) {
      const binary = atob(dataUrl.split(",")[1]);
      const bytes = new Uint8Array(binary.length);

      for (let index = 0; index < binary.length; index += 1) {
        bytes[index] = binary.charCodeAt(index);
      }

      return bytes;
    },
    safeFileName(value) {
      return String(value || "document")
        .trim()
        .replace(/[^a-z0-9._-]+/gi, "-")
        .replace(/^-+|-+$/g, "")
        || "document";
    },
    qrCompanionPdfFileName(documentation) {
      const reference = documentation && documentation.product_reference
        ? documentation.product_reference
        : "documentation";

      return `${this.safeFileName(reference)}-qr-documentation.pdf`;
    },
    downloadBlob(blob, fileName) {
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    printPdfBlob(blob) {
      const url = URL.createObjectURL(blob);
      const frame = document.createElement("iframe");
      let isCleaned = false;
      const cleanup = () => {
        if (isCleaned) {
          return;
        }

        isCleaned = true;
        setTimeout(() => {
          URL.revokeObjectURL(url);
          if (frame.parentNode) {
            frame.parentNode.removeChild(frame);
          }
        }, 1000);
      };

      frame.style.border = "0";
      frame.style.height = "0";
      frame.style.position = "fixed";
      frame.style.right = "0";
      frame.style.bottom = "0";
      frame.style.width = "0";

      frame.onload = () => {
        const frameWindow = frame.contentWindow;
        if (!frameWindow) {
          cleanup();
          return;
        }

        const afterPrint = () => {
          frameWindow.removeEventListener("afterprint", afterPrint);
          cleanup();
        };

        frameWindow.addEventListener("afterprint", afterPrint);
        frameWindow.focus();
        frameWindow.print();
        setTimeout(cleanup, 60000);
      };

      document.body.appendChild(frame);
      frame.src = url;
    },
    publicUrl(documentation) {
      if (documentation.uuid) {
        return `${this.apiBaseUrl}product-documentations/${documentation.uuid}/download`;
      }

      return documentation.download_url;
    },
    qrUrl(documentation) {
      if (documentation.qr_url) {
        return documentation.qr_url;
      }

      if (
        documentation.accessibility_url
        && documentation.accessibility_url !== this.normalizedShlinkBaseUrl()
      ) {
        return documentation.accessibility_url;
      }

      return documentation.current_version && documentation.current_version.storage_url
        ? documentation.current_version.storage_url
        : null;
    },
    illustrationUrl(documentation) {
      if (documentation.uuid) {
        return this.withDocumentationCacheKey(
          `${this.apiBaseUrl}product-documentations/${documentation.uuid}/illustration`,
          documentation
        );
      }

      return this.withDocumentationCacheKey(documentation.illustration_url, documentation);
    },
    withDocumentationCacheKey(url, documentation) {
      if (!url || !documentation.updated_at) {
        return url;
      }

      const separator = url.indexOf("?") === -1 ? "?" : "&";
      return `${url}${separator}v=${encodeURIComponent(documentation.updated_at)}`;
    },
    fileIcon(documentation) {
      const extension = documentation.current_version
        ? documentation.current_version.extension
        : "";

      if (extension === "pdf") {
        return "fas fa-file-pdf";
      }
      if (["jpg", "jpeg", "png", "webp"].includes(extension)) {
        return "fas fa-file-image";
      }
      if (["doc", "docx"].includes(extension)) {
        return "fas fa-file-word";
      }
      if (["xls", "xlsx"].includes(extension)) {
        return "fas fa-file-excel";
      }

      return "fas fa-file-alt";
    },
    formatDate(value) {
      if (!value) {
        return "";
      }

      return new Date(value).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },
    formatSize(value) {
      const size = Number(value || 0);
      if (size < 1024) {
        return `${size} o`;
      }
      if (size < 1024 * 1024) {
        return `${(size / 1024).toFixed(1)} Ko`;
      }

      return `${(size / 1024 / 1024).toFixed(1)} Mo`;
    },
    notifyRequestError(error, fallbackMessage) {
      const errors = error.response && error.response.data && error.response.data.errors;
      if (errors) {
        const firstField = Object.keys(errors)[0];
        if (firstField && errors[firstField][0]) {
          this.notifyError(errors[firstField][0]);
          return;
        }
      }

      this.notifyError(fallbackMessage);
    },
    notifyError(message) {
      this.$notify({
        message,
        type: "danger",
        verticalAlign: "bottom",
      });
    },
    clearFileInput(refName) {
      const input = Array.isArray(this.$refs[refName])
        ? this.$refs[refName][0]
        : this.$refs[refName];

      if (input) {
        input.value = "";
      }
    },
  },
};
</script>

<style scoped>
.documentation-list-toolbar {
  --documentation-control-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.documentation-toolbar-main {
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
}

.documentation-toolbar-title {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 9px;
  min-width: 0;
}

.documentation-toolbar-title .card-title {
  color: #252a31;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.2;
  margin: 0;
}

.documentation-toolbar-count {
  align-items: center;
  background: #fff3ef;
  border: 1px solid #ffd5c8;
  border-radius: 999px;
  color: #9a3412;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 800;
  gap: 4px;
  height: 26px;
  line-height: 1;
  padding: 0 10px;
}

.documentation-toolbar-count strong {
  color: #f96332;
  font-size: 14px;
}

.documentation-add-button {
  flex: 0 0 auto;
  margin: 0;
}

.documentation-toolbar-filters {
  align-items: center;
  display: grid;
  gap: 8px;
  grid-template-columns: minmax(180px, 1fr) minmax(230px, 300px) auto;
}

.documentation-search-wrap {
  align-items: center;
  display: flex;
  min-width: 0;
  position: relative;
}

.documentation-search-wrap i {
  color: #98a2b3;
  font-size: 12px;
  left: 12px;
  pointer-events: none;
  position: absolute;
  z-index: 1;
}

.documentation-search {
  border-radius: var(--documentation-control-radius);
  font-size: 13px;
  height: 36px;
  padding-left: 34px;
}

.documentation-product-filter {
  max-width: 300px;
  min-width: 230px;
  width: 100%;
}

.documentation-product-filter >>> .el-input {
  border-radius: var(--documentation-control-radius);
}

.documentation-product-filter >>> .el-input__inner {
  background-color: transparent !important;
  border: 1px solid #d3d3d3 !important;
  border-radius: var(--documentation-control-radius) !important;
  box-shadow: none !important;
  color: #2c2c2c !important;
  font-size: 13px;
  font-weight: 400;
  height: 36px;
  line-height: 36px;
  margin: 0;
  transition: border-color 0.16s ease, color 0.16s ease;
}

.documentation-product-filter >>> .el-input__inner::placeholder {
  color: #9a9a9a;
  opacity: 1;
}

.documentation-product-filter >>> .el-input__inner::-webkit-input-placeholder {
  color: #9a9a9a;
}

.documentation-product-filter >>> .el-input__inner:-ms-input-placeholder {
  color: #9a9a9a;
}

.documentation-product-filter >>> .el-select__caret {
  color: #f96332;
  font-weight: 800;
}

.documentation-product-filter:hover >>> .el-input__inner,
.documentation-product-filter.el-select:hover >>> .el-input__inner,
.documentation-product-filter >>> .el-input:hover .el-input__inner,
.documentation-product-filter >>> .el-input__inner:hover {
  background-color: transparent !important;
  border-color: #d3d3d3 !important;
  box-shadow: none !important;
  color: #2c2c2c !important;
}

.documentation-product-filter >>> .el-input__inner:focus,
.documentation-product-filter >>> .el-input.is-focus .el-input__inner {
  background-color: transparent !important;
  border-color: #f96332 !important;
  box-shadow: none !important;
  color: #2c2c2c !important;
}

.documentation-product-filter >>> .el-input__suffix {
  align-items: center;
  background: transparent;
  display: flex;
}

.documentation-product-filter >>> .el-input.is-disabled .el-input__inner {
  background: #f8fafc;
  border-color: #e4e7ec;
  box-shadow: none;
  color: #98a2b3;
}

.documentation-language-filter {
  align-items: center;
  background: #f8fafc;
  border: 1px solid #d8dee6;
  border-radius: 8px;
  display: inline-flex;
  flex: 0 0 auto;
  gap: 3px;
  height: 36px;
  max-width: none;
  min-width: 0;
  padding: 3px;
}

.language-filter-choice {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 6px;
  color: #667085;
  cursor: pointer;
  display: inline-flex;
  font-size: 11px;
  font-weight: 800;
  gap: 5px;
  height: 28px;
  justify-content: center;
  line-height: 1;
  min-width: 34px;
  padding: 0 7px;
}

.language-filter-choice.active,
.language-filter-choice:focus,
.language-filter-choice:hover {
  background: #fff;
  box-shadow: 0 1px 4px rgba(30, 36, 45, 0.12);
  color: #f96332;
  outline: 0;
}

.documentation-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
}

.documentation-card {
  background: #fff;
  border: 1px solid #e1e6ed;
  border-radius: 8px;
  box-shadow: 0 8px 18px rgba(30, 36, 45, 0.05);
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 15px;
}

.documentation-card-top {
  align-items: flex-start;
  display: flex;
  gap: 10px;
  margin-bottom: 6px;
  min-height: 0;
}

.documentation-card-heading {
  flex: 1 1 auto;
  min-width: 0;
}

.documentation-card-heading h5 {
  color: #252a31;
  display: -webkit-box;
  font-size: 15px;
  font-weight: 800;
  line-height: 1.18;
  margin: 0 0 3px;
  overflow: hidden;
  text-transform: uppercase;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.documentation-language-badge {
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e1e7ef;
  border-radius: 7px;
  color: #344054;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 11px;
  font-weight: 800;
  gap: 5px;
  height: 30px;
  line-height: 1;
  padding: 0 8px;
}

.language-flag {
  border: 1px solid rgba(16, 24, 40, 0.16);
  border-radius: 3px;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.18);
  display: inline-block;
  flex: 0 0 auto;
  height: 14px;
  line-height: 1;
  overflow: hidden;
  width: 20px;
}

.language-flag-fr {
  background: linear-gradient(90deg, #0055a4 0 33.33%, #fff 33.33% 66.66%, #ef4135 66.66% 100%);
}

.language-flag-en {
  background:
    linear-gradient(27deg, transparent 0 41%, #fff 41% 47%, #cf142b 47% 53%, #fff 53% 59%, transparent 59%),
    linear-gradient(153deg, transparent 0 41%, #fff 41% 47%, #cf142b 47% 53%, #fff 53% 59%, transparent 59%),
    linear-gradient(90deg, transparent 0 39%, #fff 39% 61%, transparent 61%),
    linear-gradient(0deg, transparent 0 35%, #fff 35% 65%, transparent 65%),
    linear-gradient(90deg, transparent 0 45%, #cf142b 45% 55%, transparent 55%),
    linear-gradient(0deg, transparent 0 43%, #cf142b 43% 57%, transparent 57%),
    #012169;
}

.language-flag-es {
  background: linear-gradient(180deg, #aa151b 0 25%, #f1bf00 25% 75%, #aa151b 75% 100%);
}

.documentation-product-line {
  align-items: baseline;
  display: flex;
  gap: 10px;
  justify-content: space-between;
  min-width: 0;
}

.documentation-product-line strong {
  color: #101828;
  display: block;
  flex: 1 1 auto;
  font-size: 14px;
  font-weight: 800;
  line-height: 1.2;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.documentation-card-reference {
  color: #667085;
  flex: 0 0 auto;
  font-size: 11px;
  font-weight: 800;
  line-height: 1;
  margin-left: auto;
  max-width: 72px;
  overflow: hidden;
  text-align: right;
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;
}

.documentation-visual {
  align-items: center;
  background: #fff;
  display: flex;
  height: 118px;
  justify-content: center;
  margin-bottom: 12px;
  overflow: hidden;
  position: relative;
}

.documentation-visual img {
  display: block;
  height: 100%;
  max-width: 92%;
  object-fit: contain;
  width: 100%;
}

.documentation-visual-qr {
  align-items: center;
  background: #fff;
  border: 1px solid #edf0f2;
  border-radius: 8px;
  display: flex;
  height: 104px;
  justify-content: center;
  padding: 8px;
  width: 104px;
}

.documentation-visual-qr >>> svg {
  display: block;
  height: 88px;
  width: 88px;
}

.documentation-visual-placeholder {
  align-items: center;
  background: #fbfcfd;
  border: 1px dashed #d8dee6;
  border-radius: 8px;
  color: #667085;
  display: inline-flex;
  gap: 10px;
  height: 72px;
  justify-content: center;
  max-width: 230px;
  padding: 0 16px;
  width: calc(100% - 16px);
}

.documentation-visual-placeholder i {
  align-items: center;
  background: #fff4f1;
  border-radius: 7px;
  color: #f96332;
  display: flex;
  flex: 0 0 34px;
  font-size: 16px;
  height: 34px;
  justify-content: center;
  width: 34px;
}

.documentation-visual-placeholder span {
  font-size: 12px;
  font-weight: 800;
  line-height: 1.2;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.documentation-document-icon {
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e1e7ef;
  border-radius: 7px;
  color: #f96332;
  display: flex;
  flex: 0 0 38px;
  font-size: 18px;
  height: 38px;
  justify-content: center;
  width: 38px;
}

.documentation-card-description {
  color: #6c757d;
  display: -webkit-box;
  font-size: 12px;
  line-height: 1.35;
  margin: 0 8px 34px;
  min-height: 16px;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
}

.documentation-version-card {
  background: #fafbfc;
  border: 1px solid #edf0f2;
  border-radius: 8px;
  margin-bottom: 12px;
  padding: 10px 12px;
}

.version-card-header {
  align-items: center;
  display: flex;
  gap: 10px;
  justify-content: space-between;
  margin-bottom: 7px;
}

.version-card-header span {
  color: #6b7580;
  font-size: 11px;
  font-weight: 800;
  line-height: 1.3;
  text-transform: uppercase;
}

.version-card-header strong {
  background: #fff4f1;
  border: 1px solid rgba(249, 99, 50, 0.18);
  border-radius: 999px;
  color: #f96332;
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 800;
  line-height: 1;
  padding: 5px 8px;
}

.version-card-file {
  align-items: center;
  color: #252a31;
  display: flex;
  font-size: 13px;
  font-weight: 700;
  gap: 7px;
  min-width: 0;
}

.version-card-file i {
  color: #f96332;
  flex: 0 0 auto;
}

.version-card-file span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.version-card-details {
  align-items: center;
  color: #6c757d;
  display: flex;
  font-size: 12px;
  font-weight: 600;
  gap: 10px;
  justify-content: space-between;
  line-height: 1.25;
  margin-top: 7px;
  min-width: 0;
}

.version-card-details span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.documentation-card-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  margin-top: auto;
  padding-top: 12px;
}

.documentation-card-actions .btn {
  align-items: center;
  border-radius: 7px;
  display: inline-flex;
  height: 35px;
  justify-content: center;
  min-width: 36px;
  padding: 6px 8px;
}

.documentation-card-actions .btn-primary {
  background: #ff6537;
  border-color: #ff6537;
}

.documentation-card-actions .btn-primary:hover,
.documentation-card-actions .btn-primary:focus {
  background: #f45628;
  border-color: #f45628;
}

.documentation-card-actions .btn-default {
  background: #85898e;
  border-color: #85898e;
  color: #fff;
}

.documentation-card-actions .btn-default:hover,
.documentation-card-actions .btn-default:focus {
  background: #747a80;
  border-color: #747a80;
  color: #fff;
}

.documentation-history {
  border-top: 1px solid #eceff3;
  margin-top: 16px;
  padding-top: 12px;
}

.history-row {
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  padding: 9px 0;
}

.history-row > div:first-child {
  min-width: 0;
}

.history-row + .history-row {
  border-top: 1px solid #f0f2f4;
}

.history-row strong,
.history-row span,
.history-row small {
  display: block;
}

.history-row strong {
  color: #252a31;
  font-size: 13px;
}

.history-row span {
  color: #626c77;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.history-row small {
  color: #8a949f;
  font-size: 12px;
  margin-top: 2px;
}

.history-actions {
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  gap: 6px;
}

.history-icon-button {
  align-items: center;
  border: 0;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  height: 28px;
  justify-content: center;
  width: 28px;
}

.history-download {
  background: #f96332;
  color: #fff;
}

.history-edit {
  background: #f4f6f8;
  color: #4f5965;
}

.history-delete {
  background: #fff4f1;
  color: #e23d28;
}

.documentation-empty {
  align-items: center;
  border: 1px dashed #ccd3da;
  border-radius: 8px;
  color: #6c757d;
  display: flex;
  justify-content: center;
  min-height: 180px;
}

.selected-documentation {
  background: #fafbfc;
  border: 1px solid #edf0f2;
  border-radius: 8px;
  margin-bottom: 16px;
  padding: 12px;
}

.selected-documentation span {
  color: #6c757d;
  display: block;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 4px;
}

.selected-documentation strong {
  color: #252a31;
  display: block;
}

.form-section-icon {
  align-items: center;
  background: #fff4f1;
  color: #f96332;
  display: flex;
  flex: 0 0 auto;
  justify-content: center;
}

.form-section-header small {
  color: #7a8591;
  display: block;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.35;
}

.form-section-header {
  align-items: center;
  border-top: 1px solid #eef1f4;
  display: flex;
  gap: 8px;
  margin: 12px 0 8px;
  padding-top: 10px;
}

.documentation-form > .form-section-header:first-child {
  border-top: 0;
  margin-top: 0;
  padding-top: 0;
}

.form-section-icon {
  border-radius: 7px;
  font-size: 14px;
  height: 28px;
  width: 28px;
}

.form-section-header strong {
  color: #252a31;
  display: block;
  font-size: 14px;
  font-weight: 800;
  line-height: 1.25;
}

.compact-section-grid {
  gap: 0;
}

.field-helper {
  color: #77828f;
  display: block;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.35;
  margin-top: 5px;
}

.file-upload-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

.single-upload-grid {
  grid-template-columns: minmax(0, 1fr);
}

.current-illustration-preview {
  align-items: center;
  border: 1px solid #edf0f2;
  border-radius: 8px;
  display: grid;
  gap: 10px;
  grid-template-columns: 64px minmax(0, 1fr) auto;
  margin-bottom: 8px;
  min-height: 64px;
  padding: 8px;
}

.current-illustration-preview img {
  border-radius: 7px;
  height: 48px;
  object-fit: cover;
  width: 48px;
}

.current-illustration-preview span {
  color: #4f5965;
  font-size: 13px;
  font-weight: 700;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.qr-modal-shell {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.qr-header-card {
  align-items: center;
  background: #f8fafb;
  border: 1px solid #e5e9ef;
  border-radius: 8px;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  padding: 12px 14px;
}

.qr-documentation-summary {
  min-width: 0;
}

.qr-documentation-summary span,
.qr-documentation-summary small {
  color: #7a8591;
  display: block;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.35;
}

.qr-documentation-summary strong {
  color: #252a31;
  display: block;
  font-size: 16px;
  font-weight: 800;
  line-height: 1.25;
  margin: 2px 0;
  overflow-wrap: anywhere;
}

.qr-ready-badge {
  align-items: center;
  background: #ecfdf5;
  border: 1px solid #bbf7d0;
  border-radius: 999px;
  color: #047857;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 800;
  gap: 6px;
  line-height: 1;
  padding: 8px 10px;
}

.qr-modal-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(210px, 0.85fr) minmax(0, 1.15fr);
}

.qr-preview-panel,
.qr-url-panel {
  min-width: 0;
}

.qr-code-preview {
  align-items: center;
  background: #fff;
  border: 1px solid #e4e8ee;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  min-height: 250px;
  padding: 16px;
  width: 100%;
}

.qr-code-preview >>> svg {
  height: auto;
  max-width: 220px;
  width: 100%;
}

.qr-url-panel {
  background: #fff;
  border: 1px solid #edf0f2;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 14px;
}

.qr-url-input {
  border: 0;
  color: #252a31;
  flex: 1;
  font-size: 12px;
  font-weight: 700;
  height: 44px;
  min-width: 0;
  outline: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.qr-url-copy-row {
  align-items: center;
  border: 1px solid #d8dee6;
  border-radius: 8px;
  display: flex;
  min-width: 0;
  overflow: hidden;
  padding-left: 12px;
}

.qr-url-copy-row:focus-within {
  border-color: #0f766e;
  box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.12);
}

.qr-url-prefix {
  color: #0f766e;
  flex: 0 0 auto;
  font-size: 14px;
  margin-right: 8px;
}

.qr-action-grid {
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-top: 10px;
}

.qr-action-card {
  align-items: center;
  background: #f8fafb;
  border: 1px solid #e3e8ef;
  border-radius: 8px;
  color: #344054;
  cursor: pointer;
  display: inline-flex;
  font-size: 12px;
  font-weight: 800;
  gap: 7px;
  height: 44px;
  justify-content: center;
  line-height: 1;
  min-width: 0;
  padding: 0 8px;
}

.qr-action-card:hover {
  background: #eef3f7;
}

.qr-action-primary {
  background: #0f766e;
  border-color: #0f766e;
  color: #fff;
}

.qr-action-primary:hover {
  background: #0b5f59;
}

.qr-url-meta {
  align-items: flex-start;
  color: #687482;
  display: flex;
  font-size: 12px;
  font-weight: 600;
  gap: 8px;
  line-height: 1.4;
  margin-top: 12px;
}

.qr-companion-meta {
  align-items: flex-start;
  background: #f8fafc;
  border: 1px solid #e3e8ef;
  border-radius: 8px;
  color: #344054;
  display: flex;
  font-size: 12px;
  font-weight: 700;
  gap: 8px;
  line-height: 1.4;
  margin-top: 10px;
  padding: 9px 10px;
}

.qr-companion-meta i {
  color: #f96332;
  flex: 0 0 auto;
  margin-top: 2px;
}

.qr-companion-meta span {
  min-width: 0;
  overflow-wrap: anywhere;
}

.qr-url-meta i {
  color: #0f766e;
  flex: 0 0 auto;
  margin-top: 2px;
}

.qr-url-meta span {
  min-width: 0;
  overflow-wrap: anywhere;
}

.product-documentation-page >>> .modal.show {
  align-items: center;
  display: flex !important;
  justify-content: center;
  overflow-y: auto;
  padding: 24px 12px;
}

.product-documentation-page >>> .documentation-modal-dialog {
  margin: 0 auto;
  max-width: 940px;
  width: calc(100% - 24px);
}

.product-documentation-page >>> .documentation-modal-dialog .modal-content,
.product-documentation-page >>> .qr-modal-dialog .modal-content {
  border: 0;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 32px);
  box-shadow: 0 24px 70px rgba(24, 31, 42, 0.24);
  overflow: hidden;
}

.product-documentation-page >>> .documentation-modal-dialog .modal-header,
.product-documentation-page >>> .qr-modal-dialog .modal-header {
  align-items: center;
  border-bottom: 1px solid #edf0f2;
  flex: 0 0 auto;
  padding: 14px 66px 12px 22px;
  position: relative;
}

.product-documentation-page >>> .documentation-modal-dialog .modal-title,
.product-documentation-page >>> .qr-modal-dialog .modal-title {
  color: #252a31;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
  margin: 0;
}

.product-documentation-page >>> .documentation-modal-dialog .close,
.product-documentation-page >>> .qr-modal-dialog .close {
  align-items: center;
  background: #fff4f1;
  border-radius: 50%;
  color: #f96332;
  display: flex;
  height: 34px;
  justify-content: center;
  margin: 0;
  opacity: 1;
  padding: 0;
  position: absolute;
  right: 22px;
  top: 50%;
  transform: translateY(-50%);
  width: 34px;
}

.product-documentation-page >>> .documentation-modal-dialog .close i,
.product-documentation-page >>> .qr-modal-dialog .close i {
  font-size: 14px;
}

.product-documentation-page >>> .documentation-modal-dialog .modal-body {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 16px 22px 8px;
}

.product-documentation-page >>> .documentation-modal-dialog .modal-footer,
.product-documentation-page >>> .qr-modal-dialog .modal-footer {
  align-items: center;
  border-top: 1px solid #edf0f2;
  display: flex;
  flex: 0 0 auto;
  gap: 8px;
  justify-content: flex-end;
  padding: 12px 22px 14px;
}

.product-documentation-page >>> .documentation-modal-dialog .modal-footer .btn,
.product-documentation-page >>> .qr-modal-dialog .modal-footer .btn {
  border-radius: 6px;
  min-width: 112px;
}

.product-documentation-page >>> .qr-modal-dialog {
  margin: 0 auto;
  max-width: 720px;
  width: calc(100% - 24px);
}

.product-documentation-page >>> .documentation-modal-body {
  padding-bottom: 8px;
}

.product-documentation-page >>> .qr-modal-body {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 16px 22px 8px;
  text-align: left;
}

.modern-form-grid {
  display: grid;
  gap: 12px 16px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.version-form-grid {
  grid-template-columns: 220px minmax(0, 1fr);
}

.modern-field {
  min-width: 0;
}

.modern-field-wide {
  grid-column: span 1;
}

.modern-field-full {
  grid-column: 1 / -1;
}

.modern-field-textarea {
  grid-column: span 1;
}

.modern-field label {
  color: #344054;
  display: block;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.3;
  margin: 0 0 5px;
}

.modern-input-wrap {
  align-items: center;
  background: #fff;
  border: 1px solid #d8dee6;
  border-radius: 8px;
  display: flex;
  min-height: 40px;
  padding: 0 12px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.modern-input-wrap:focus-within,
.modern-textarea:focus,
.upload-dropzone:focus-within,
.upload-dropzone:hover {
  border-color: #f96332;
  box-shadow: 0 0 0 3px rgba(249, 99, 50, 0.12);
}

.modern-input-wrap i {
  color: #8a949f;
  flex: 0 0 auto;
  font-size: 16px;
  margin-right: 10px;
}

.modern-input-wrap input {
  border: 0;
  color: #252a31;
  flex: 1;
  font-size: 14px;
  height: 38px;
  min-width: 0;
  outline: 0;
}

.language-selector {
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.language-choice {
  align-items: center;
  background: #fff;
  border: 1px solid #d8dee6;
  border-radius: 8px;
  color: #344054;
  cursor: pointer;
  display: inline-flex;
  font-size: 12px;
  font-weight: 800;
  gap: 6px;
  height: 40px;
  justify-content: center;
  min-width: 0;
  padding: 0 8px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}

.language-choice span:last-child {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.current-companion-preview {
  align-items: center;
  border: 1px solid #edf0f2;
  border-radius: 8px;
  display: grid;
  gap: 10px;
  grid-template-columns: 48px minmax(0, 1fr) auto;
  margin-bottom: 8px;
  min-height: 64px;
  padding: 8px;
}

.current-companion-preview i {
  align-items: center;
  background: #fff4f1;
  border-radius: 7px;
  color: #f96332;
  display: flex;
  font-size: 18px;
  height: 48px;
  justify-content: center;
  width: 48px;
}

.current-companion-preview span {
  color: #4f5965;
  font-size: 13px;
  font-weight: 700;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.current-file-delete-button {
  align-items: center;
  background: #fff4f1;
  border: 0;
  border-radius: 7px;
  color: #e23d28;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 14px;
  height: 34px;
  justify-content: center;
  width: 34px;
}

.current-file-delete-button:hover,
.current-file-delete-button:focus {
  background: #fee2dc;
  color: #c92f1f;
  outline: 0;
}

.current-file-delete-button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.language-choice.active,
.language-choice:focus,
.language-choice:hover {
  background: #fff4f1;
  border-color: #f96332;
  box-shadow: 0 0 0 3px rgba(249, 99, 50, 0.12);
  color: #f96332;
  outline: 0;
}

.modern-input-wrap input::placeholder,
.modern-textarea::placeholder {
  color: #b4bcc6;
}

.modern-textarea {
  border: 1px solid #d8dee6;
  border-radius: 8px;
  color: #252a31;
  font-size: 14px;
  min-height: 72px;
  outline: 0;
  padding: 9px 12px;
  resize: vertical;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  width: 100%;
}

.documentation-file-input {
  height: 1px;
  opacity: 0;
  overflow: hidden;
  position: absolute;
  width: 1px;
}

.upload-dropzone {
  align-items: center;
  border: 1px dashed #c8d0da;
  border-radius: 8px;
  cursor: pointer;
  display: grid;
  gap: 10px;
  grid-template-columns: auto minmax(0, 1fr) auto;
  margin: 0;
  min-height: 58px;
  padding: 10px 12px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}

.upload-dropzone:hover {
  background: #fffaf7;
}

.image-upload-dropzone {
  background: #fbfcfd;
}

.document-upload-dropzone {
  background: #fff;
}

.companion-upload-dropzone {
  background: #fff;
}

.compact-upload {
  min-height: 42px;
  padding: 10px 12px;
}

.upload-icon {
  align-items: center;
  background: #fff4f1;
  border-radius: 8px;
  color: #f96332;
  display: flex;
  font-size: 18px;
  height: 36px;
  justify-content: center;
  width: 36px;
}

.upload-content {
  min-width: 0;
}

.upload-content strong,
.upload-content small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.upload-content strong {
  color: #252a31;
  font-size: 14px;
  font-weight: 700;
}

.upload-content small {
  color: #8a949f;
  font-size: 12px;
  margin-top: 3px;
}

.upload-action {
  background: #f4f6f8;
  border-radius: 6px;
  color: #4f5965;
  font-size: 12px;
  font-weight: 700;
  padding: 8px 10px;
}

@media (max-width: 768px) {
  .documentation-toolbar-main {
    align-items: stretch;
    flex-direction: column;
  }

  .documentation-toolbar-filters {
    grid-template-columns: 1fr;
  }

  .documentation-add-button,
  .documentation-product-filter,
  .documentation-language-filter {
    max-width: none;
    min-width: 0;
    width: 100%;
  }

  .documentation-language-filter {
    width: 100%;
  }

  .language-filter-choice {
    flex: 1 1 0;
  }

  .modern-form-grid,
  .version-form-grid,
  .file-upload-grid,
  .qr-modal-grid {
    grid-template-columns: 1fr;
  }

  .modern-field-wide,
  .modern-field-textarea {
    grid-column: 1 / -1;
  }

  .upload-dropzone {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .upload-action {
    grid-column: 1 / -1;
    text-align: center;
  }

  .qr-header-card {
    align-items: flex-start;
    flex-direction: column;
  }

  .qr-url-panel {
    padding: 12px;
  }

  .qr-action-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .qr-action-card {
    font-size: 11px;
    gap: 5px;
    padding: 0 6px;
  }

  .qr-code-preview {
    min-height: 220px;
  }

  .product-documentation-page >>> .documentation-modal-dialog .modal-title,
  .product-documentation-page >>> .qr-modal-dialog .modal-title {
    font-size: 20px;
  }
}
</style>

<style>
.documentation-product-select-popper {
  border-color: #e1e6ed;
  border-radius: 8px;
  box-shadow: 0 12px 30px rgba(30, 36, 45, 0.14);
  overflow: hidden;
}

.documentation-product-select-popper .el-select-dropdown__list {
  padding: 5px;
}

.documentation-product-select-popper .el-select-dropdown__item {
  border-radius: 7px;
  height: auto;
  line-height: normal;
  margin: 2px 0;
  padding: 0;
}

.documentation-product-select-popper .el-select-dropdown__item.hover,
.documentation-product-select-popper .el-select-dropdown__item:hover {
  background: #f8fafc;
}

.documentation-product-select-popper .el-select-dropdown__item.selected {
  background: #fff3ef;
  color: #f96332;
}

.documentation-product-select-popper .product-filter-option {
  align-items: center;
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(0, 1fr) auto;
  min-height: 56px;
  padding: 9px 10px;
}

.documentation-product-select-popper .product-filter-option-copy {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.documentation-product-select-popper .product-filter-option-copy strong {
  color: #252a31;
  display: block;
  font-size: 13px;
  font-weight: 800;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.documentation-product-select-popper .product-filter-option-copy span {
  color: #667085;
  display: block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;
}

.documentation-product-select-popper .product-filter-option-count {
  align-items: center;
  background: #fff3ef;
  border: 1px solid #ffd5c8;
  border-radius: 999px;
  color: #f96332;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 11px;
  font-weight: 800;
  height: 24px;
  line-height: 1;
  padding: 0 8px;
}
</style>
